import { Alert } from "@gigpro/ui/Alert";
import { Button } from "@gigpro/ui/Button";
import { AssignmentTurnedIn, Heart, HeartOutline } from "@gigpro/ui/Icons";
import { Skeleton } from "@gigpro/ui/Skeleton";
import { formatPhoneNumber } from "@gigpro/utils";
import type { GigsterProfileReadonly } from "@sidegigllc/api/client";
import type { ProBadge } from "api/resources/pros";
import { CopyButton } from "domains/admin/components/CopyButton";
import { InNetworkChip } from "domains/connect/components/InNetworkChip";
import { ProAvatar } from "domains/pros/components/Avatar";
import { ProCompletionRate } from "domains/pros/components/CompletionRate";
import { RatingDisplay } from "domains/pros/components/RatingDisplay";
import type React from "react";
import { ProBadges } from "../ProBadges";

type PROfileHeaderProps = {
  data: Pick<
    GigsterProfileReadonly,
    | "display_name"
    | "headshot_public_id"
    | "performance"
    | "is_favorite"
    | "is_blocked"
    | "has_worked_together"
    | "favorite_id"
    | "user_id"
    | "badges"
  > & { in_network: boolean; phone_number?: string; market_name: string | undefined };
  onSendOffer: () => void;
  onMessage: () => void;
  onFavoriteMember: (favoriteId: number) => void;
  onUnFavoriteMember: (favoriteId: number) => void;
  messagingLoading: boolean;
  hideFavorite?: boolean;
  hideButtons?: boolean;
  showAdminData?: boolean;
  /** When provided (admin only), renders the pro's full name in the header instead of the abbreviated display name. */
  fullName?: string;
  /** Additional buttons to render in the action row */
  extraButtons?: React.ReactNode;
};

export const PROfileHeader: React.FC<PROfileHeaderProps> = ({
  data: {
    display_name,
    headshot_public_id,
    performance: { rating, completion_percentage, gigs_worked },
    is_favorite,
    is_blocked,
    has_worked_together,
    favorite_id,
    user_id,
    badges,
    phone_number,
    in_network: inNetwork,
    market_name,
  },
  messagingLoading,
  onMessage,
  onSendOffer,
  onFavoriteMember,
  onUnFavoriteMember,
  hideFavorite,
  hideButtons,
  showAdminData,
  fullName,
  extraButtons,
}) => (
  <div className="flex flex-col">
    <div className="flex items-center gap-2 pb-2 @sm/profile:gap-6">
      <ProAvatar className="size-12 @sm/profile:size-24" size={120} imageId={headshot_public_id} />
      <div className="flex w-full min-w-0 flex-col @sm/profile:gap-2">
        <div className="flex items-center justify-between">
          <h1 className="truncate font-semibold @sm/profile:text-enormous">{fullName ?? display_name}</h1>
          <ProBadges
            badges={badges?.filter((badge): badge is ProBadge[number] => badge !== "TOP_PRO").toReversed()}
            className="h-6 py-1"
          />
          {!hideFavorite && (
            <Button
              icon
              className="ml-auto"
              onClick={() => {
                if (is_favorite && favorite_id) onUnFavoriteMember(favorite_id);
                else if (!is_favorite) onFavoriteMember(user_id);
              }}
              data-testid="button_favorite"
            >
              {is_favorite ? (
                <Heart className="size-4 text-error @sm/profile:size-5" />
              ) : (
                <HeartOutline className="size-4 text-error @sm/profile:size-5" />
              )}
            </Button>
          )}
        </div>
        {inNetwork && <InNetworkChip />}
        <div className="flex flex-row items-center gap-2">
          <RatingDisplay rating={rating} className="@sm/profile:text-large" />
          <p className="flex flex-row items-center gap-1 truncate @sm/profile:text-large">
            <AssignmentTurnedIn className="size-3 text-medium-gray @sm/profile:mb-0.5 @sm/profile:size-4" />
            <strong>{gigs_worked ?? 0}</strong> shifts done
          </p>
        </div>
        <ProCompletionRate completionRate={completion_percentage} />
      </div>
    </div>
    {(has_worked_together || is_blocked) && (
      <Alert severity={is_blocked ? "error" : "info"} size="small" className="w-fit rounded-full">
        {is_blocked ? "You have blocked this pro" : `${display_name} has worked with you before`}
      </Alert>
    )}

    {showAdminData && (
      <p className="mt-2 flex flex-wrap items-center gap-1 [&>b]:border-l-2 [&>b]:pl-1">
        <b>Worker ID:</b> <CopyButton className="text-normal" copyValue={`${user_id}`} />
        <b>Phone:</b>
        {phone_number ? (
          <CopyButton className="text-normal" copyValue={formatPhoneNumber(phone_number, { hasNoPrefix: true })} />
        ) : (
          "-"
        )}
        <b>Market:</b>
        {market_name ? <CopyButton className="text-normal" copyValue={market_name} /> : "-"}
      </p>
    )}
    {!hideButtons && (
      <div className="mt-3 flex flex-row gap-3 @sm/profile:mt-5">
        <Button onClick={onMessage} variant="contained" loading={messagingLoading} data-testid="button_message">
          Message
        </Button>
        <Button onClick={onSendOffer} variant="outlined" disabled={is_blocked} data-testid="button_offergig">
          Send offer
        </Button>
        {extraButtons}
      </div>
    )}
  </div>
);

export function PROfileHeaderSkeleton() {
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-4 pb-2 @sm/profile:gap-6">
        <Skeleton variant="circle" className="size-12 @sm/profile:size-24" />
        <div className="flex w-full flex-col text-normal @sm/profile:gap-2 @sm/profile:text-large">
          <Skeleton className="w-26 @sm/profile:text-enormous" />

          <div className="flex flex-row items-center gap-2">
            <Skeleton className="w-8" />
            <Skeleton className="w-24" />
          </div>
          <Skeleton className="w-26" />
        </div>
      </div>
      <div className="mt-3 flex flex-row gap-3 @sm/profile:mt-5">
        <Skeleton className="h-8 w-16" />
        <Skeleton className="h-8 w-16" />
      </div>
    </div>
  );
}
